import { Logger } from "./Logger";

export interface ColumnPreference {
    mandatoryColumns: string[];
    optionalColumns: string[];
    allColumns: string[];
}

export class ColumnPreferenceManager {
    private static readonly COOKIE_PREFIX = "ds-cp-";
    private static readonly COOKIE_EXPIRY_DAYS = 1;

    /**
     * Get column preferences for a specific dataset
     */
    static getColumnPreferences(dataSetName: string): ColumnPreference {
        try {
            const cookieName = this.COOKIE_PREFIX + dataSetName;
            const cookieValue = this.getCookie(cookieName);
            
            // Get default mandatory columns based on dataset
            const getDefaultMandatoryColumns = (dataset: string): string[] => {
                // Handle all campaign-runner variants (campaign-runner, campaign-runner-scheduled, etc.)
                if (dataset === 'campaign-runner' || dataset.startsWith('campaign-runner-')) {
                    return ['campaignName', 'campaignType', 'startTimestamp', 'status'];
                }
                // Handle datasets-list (DataSets listing page)
                if (dataset === 'datasets-list') {
                    return ['dataSetName', 'collectionStatus', 'accessLevel', 'contactsCount'];
                }
                // Default columns for other datasets
                return ['firstName', 'lastName', 'primaryMobile', 'primaryEmail'];
            };
            
            const defaultMandatoryColumns = getDefaultMandatoryColumns(dataSetName);
            
            if (!cookieValue) {
                return {
                    mandatoryColumns: defaultMandatoryColumns,
                    optionalColumns: [],
                    allColumns: defaultMandatoryColumns
                };
            }

            const preferences = JSON.parse(cookieValue);
            const isCampaignRunner = dataSetName === 'campaign-runner' || dataSetName.startsWith('campaign-runner-');
            const isDatasetsList = dataSetName === 'datasets-list';
            
            // For campaign-runner and datasets-list, ALWAYS use the default mandatory columns
            // This ensures the correct columns are shown even if there's an old cookie
            const enforcedMandatory = (isCampaignRunner || isDatasetsList)
                ? defaultMandatoryColumns
                : (preferences.mandatoryColumns || defaultMandatoryColumns);
            
            const optional = preferences.optionalColumns || [];
            const all = Array.from(new Set([...enforcedMandatory, ...optional]));
            return {
                mandatoryColumns: enforcedMandatory,
                optionalColumns: optional,
                allColumns: all
            };
        } catch (error) {
            Logger.error("ColumnPreferenceManager: Error parsing column preferences:", error);
            const isCampaignRunner = dataSetName === 'campaign-runner' || dataSetName.startsWith('campaign-runner-');
            const isDatasetsList = dataSetName === 'datasets-list';
            let defaultMandatoryColumns: string[];
            if (isCampaignRunner) {
                defaultMandatoryColumns = ['campaignName', 'campaignType', 'startTimestamp', 'status'];
            } else if (isDatasetsList) {
                defaultMandatoryColumns = ['dataSetName', 'collectionStatus', 'accessLevel', 'contactsCount'];
            } else {
                defaultMandatoryColumns = ['firstName', 'lastName', 'primaryMobile', 'primaryEmail'];
            }
            return {
                mandatoryColumns: defaultMandatoryColumns,
                optionalColumns: [],
                allColumns: defaultMandatoryColumns
            };
        }
    }

    /**
     * Save column preferences for a specific dataset
     */
    static saveColumnPreferences(dataSetName: string, preferences: ColumnPreference): void {
        try {
            const cookieName = this.COOKIE_PREFIX + dataSetName;
            const cookieValue = JSON.stringify(preferences);
            this.setCookie(cookieName, cookieValue, this.COOKIE_EXPIRY_DAYS);
            Logger.debug(`ColumnPreferenceManager: Saved preferences for ${dataSetName}:`, preferences);
        } catch (error) {
            Logger.error("ColumnPreferenceManager: Error saving column preferences:", error);
        }
    }

    /**
     * Get available columns that can be toggled (excluding mandatory columns and system fields)
     */
    static getAvailableOptionalColumns(allSchemaColumns: string[]): string[] {
        const mandatoryColumns = ['firstName', 'lastName', 'primaryMobile', 'primaryEmail'];
        const systemFields = ['_id', 'createdAt', 'updatedAt', '__v'];
        return allSchemaColumns.filter(col => 
            !mandatoryColumns.includes(col) && 
            !systemFields.includes(col)
        );
    }

    /**
     * Update column preferences
     */
    static updateColumnPreferences(dataSetName: string, selectedOptionalColumns: string[]): ColumnPreference {
        const mandatoryColumns = ['firstName', 'lastName', 'primaryMobile', 'primaryEmail'];
        const preferences: ColumnPreference = {
            mandatoryColumns,
            optionalColumns: selectedOptionalColumns,
            allColumns: [...mandatoryColumns, ...selectedOptionalColumns]
        };
        
        this.saveColumnPreferences(dataSetName, preferences);
        return preferences;
    }

    /**
     * Get cookie value by name
     */
    private static getCookie(name: string): string | null {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) {
            return parts.pop()?.split(';').shift() || null;
        }
        return null;
    }

    /**
     * Set cookie with expiration
     */
    private static setCookie(name: string, value: string, days: number): void {
        const expires = new Date();
        expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
        document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
    }
} 